import { AccountStore } from 'store/account-store';
import { ComponentVersionDto, ComponentVersionStatus } from './raptor-component-version.dto';
import { transform } from './transformer';

export enum ComponentType {
    CATAPULT = 'Catapult',
    DOCKER_IMAGE = 'Docker-Image',
    DOCKER_FILE = 'Docker-File',
    DOCKER_COMPOSE = 'Docker-Compose',
}

export interface CreateComponentRequest {
    name: string;
    type: ComponentType;
    description?: string | null;
}

export interface CreateComponentQueryRequest {
    accountUuid: string;
}

export interface UpdateComponentRequest {
    name?: string;
    type?: ComponentType;
    description?: string | null;
}

export interface ListComponentsQueryRequest {
    accountUuid: string;
    includeSharedComponents?: boolean;
    publishedOnly?: boolean;
    type?: ComponentType;
    offset?: number;
    limit?: number;
}

export interface ListComponentVersionsQueryRequest {
    status?: ComponentVersionStatus;
    includeManifests?: boolean;
    offset?: number;
    limit?: number;
}

export interface DeleteComponentQueryRequest {
    force?: boolean;
}

export class ComponentDto {
    constructor(private readonly _object: Record<string, any>) {
    }

    get id(): string {
        return this.uuid;
    }

    get uuid(): string {
        return this._object['uuid'] || '';
    }

    get name(): string {
        return this._object['name'] || '';
    }

    get displayName(): string {
        return this.name;
    }

    get type(): ComponentType {
        if (Object.values(ComponentType).includes(this._object['type'])) {
            return this._object['type'];
        }
        return ComponentType.CATAPULT;
    }

    get description(): string | null {
        return this._object['description'] ?? null;
    }

    get accountUuid(): string {
        return this._object['accountUuid'];
    }

    get createdAt(): Date {
        return this._object['createdAt'] ? new Date(this._object['createdAt']) : null;
    }

    get updatedAt(): Date {
        return this._object['updatedAt'] ? new Date(this._object['updatedAt']) : null;
    }

    get latestComponentVersion(): ComponentVersionDto | null {
        return this._object['latestComponentVersion']
            ? transform(ComponentVersionDto, this._object['latestComponentVersion'])
            : null;
    }

    get latestVersion(): string {
        return this.latestComponentVersion?.version || '';
    }

    get raw(): Record<string, any> {
        return this._object;
    }
}

export class RaptorComponentViewModel extends ComponentDto {
    constructor(
        public readonly component: ComponentDto,
        private readonly accountStore: AccountStore,
    ) {
        super(component.raw);
    }

    get accountName(): string {
        const account = this.accountStore.getAccountByUuidOrParentUuidForClientAccount(this.accountUuid);
        if (!account) {
            return '';
        }
        if (account.uuid === this.accountUuid) {
            return account.name;
        }
        return account.parent?.name;
    }
}

export class ComponentMessageResponseDto {
    constructor(private readonly _object: Record<string, any>) {
    }

    get message(): string {
        return this._object['message'] || '';
    }
}
